import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CircularProgress, Fade } from '@mui/material';
import styled from 'styled-components';

import { useUserAgent } from '../hooks/useUserAgent';
import { isMicrosoftDocument, isRawPrefix } from '../utils/files';
import { getUrlParams } from '../utils/url';

const Container = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
`;

const Iframe = styled.iframe`
    width: 100vw;
    height: 100vh;
    border: none;
`;

const LoadingContainer = styled.div`
    width: 100vw;
    height: 100vh;
    background-color: ${(props) => props.theme.colors.white};
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface LoadingState {
    loading: boolean;
    error: Error | null;
}

const FileViewer = () => {
    const { mime_type: mimeType, url } = getUrlParams();
    const { isAndroid } = useUserAgent(navigator.userAgent);

    const [state, setState] = useState<LoadingState>({ loading: true, error: null });
    const [iframeTimeoutId, setIframeTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        if (!url || !mimeType) {
            setState({
                loading: false,
                error: new Error('Improper url format. `url` and `mime_type` parameters are required.')
            });

            return;
        }

        if (isRawPrefix(mimeType)) {
            window.location.replace(`${url}`);
        }
    }, [mimeType, url]);

    const iFrameLink = useMemo(() => {
        if (!mimeType || !url || isRawPrefix(mimeType)) return;

        const encodedUrl = encodeURIComponent(url);

        if (isMicrosoftDocument(mimeType)) {
            return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
        }

        return `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
    }, [mimeType, url]);

    const updateIframeSrc = useCallback(() => {
        if (iFrameLink && iframeRef.current) {
            iframeRef.current.src = iFrameLink;
        }
    }, [iFrameLink]);

    useEffect(() => {
        const intervalId = setInterval(updateIframeSrc, 1500);

        setIframeTimeoutId(intervalId);

        return () => clearInterval(intervalId);
    }, [updateIframeSrc]);

    const iframeLoaded = () => {
        setState({ ...state, loading: false });

        if (iframeTimeoutId) {
            clearInterval(iframeTimeoutId);
        }
    };

    /**
     * Android browsers have issue with the microsoft viewer
     * iframe redirecting the top-level window, so no iframe
     * for android microsoft documents, just a redirect to the
     * microsoft viewer.
     */
    if (isAndroid && isMicrosoftDocument(mimeType)) {
        const src = url || '';
        const qs = new URLSearchParams({ src });

        window.location.replace(`https://view.officeapps.live.com/op/view.aspx?${qs}`);

        return <></>;
    }

    return (
        <Container>
            <Fade in={state.loading}>
                <LoadingContainer>
                    <CircularProgress />
                </LoadingContainer>
            </Fade>
            <Fade in={!!state.error}>
                <LoadingContainer>{state.error?.message}</LoadingContainer>
            </Fade>
            {iFrameLink && <Iframe onLoad={iframeLoaded} onError={updateIframeSrc} ref={iframeRef} src={iFrameLink} />}
        </Container>
    );
};

export default FileViewer;
